import { formatDate } from '@angular/common';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subject, iif, throwError } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';
import { RequestRoot } from 'types';
import { FeaturesI18N } from 'controls';
import { SubscriptionUpdateResult } from 'src/app/model/subscription-update-result';
import { SubscriptionService } from 'src/app/service/subscription.service';
import { NetworkService } from 'src/app/service/network.service';
import { StatusColorMapper } from 'src/app/service/status-color-mapper';

@Component({
    selector: 'request-summary',
    templateUrl: './request-summary.component.html',
    styleUrls: ['./request-summary.component.scss'],
})
export class RequestSummaryComponent implements OnInit, OnDestroy {
    @Input() summary: RequestRoot | null = null;
    @Input() selected: boolean | null = null;
    @Output() update = new EventEmitter();

    dead$: Subject<void> = new Subject();

    constructor(
        private networkService: NetworkService,
        private subscriptionService: SubscriptionService,
        @Inject(LOCALE_ID) private locale: string
    ) {}

    ngOnInit(): void {
        if (this.summary?.uid && this.summary.uid.length === 25) {
            this.updateRequestStatus().pipe(takeUntil(this.dead$)).subscribe();
        }
    }

    ngOnDestroy(): void {
        this.dead$.next();
        this.dead$.complete();
    }

    public get id(): string {
        return this.summary?.id ?? '';
    }

    public get fullName(): string | null {
        return this.summary?.features.asString().trim() || null;
    }

    public get description(): string | null {
        if (this.summary?.status === 'DATA_ENTERED' || this.summary?.status === 'READY') {
            let editEvents = this.summary?.eventHistory?.filter((e) => e.type === 'DATA_ENTERED');

            if (editEvents && editEvents[editEvents.length - 1]) {
                let lastEditEventFormattedDate = formatDate(editEvents[editEvents.length - 1].created!, 'dd.MM.yyyy HH:mm', this.locale);
                return editEvents.length === 1 ? 'Создано: ' + lastEditEventFormattedDate : 'Изменено: ' + lastEditEventFormattedDate;
            }
        }
        return this.summary?.uid ?? '';
    }

    public get statusCode(): string {
        return this.summary?.status ?? '';
    }

    public get statusName(): string {
        return FeaturesI18N.getString('STATUS.BIOPASSPORT.' + this.summary?.status);
    }

    public get statusColor(): string {
        const summaryStatus = this.summary?.status;
        return summaryStatus ? `#${StatusColorMapper.getStatusColor(summaryStatus).hex()}` : 'transparent';
    }

    private updateRequestStatus(): Observable<void> {
        return this.networkService.waitForInternetConnection().pipe(
            concatMap(() => {
                return iif(
                    () => Boolean(this.summary?.id),
                    this.subscriptionService.updateSubscriptionStatus(this.summary?.id!, false),
                    throwError(() => new Error('Не найдено ID заявления'))
                );
            }),
            concatMap((result) =>
                iif(
                    () => Boolean(result),
                    this.finalizeUpdateSequence(result!),
                    throwError(() => new Error('Не удалось обновить результат подписки'))
                )
            )
        );
    }

    private finalizeUpdateSequence(result: SubscriptionUpdateResult): Observable<void> {
        return new Observable<void>((subscriber) => {
            if (result.notified && this.summary) {
                this.summary.eventHistory = result.request.eventHistory;
                this.summary.status = result.request.status;
                this.update.emit();
            }
            subscriber.next(void 0);
            subscriber.complete();
        });
    }
}
